import { env } from '@/lib/env';

/**
 * CSRF double-submit helpers: the server (Spring Security, `CookieCsrfTokenRepository`) hands the
 * browser a readable `XSRF-TOKEN` cookie on every response, and refuses any POST/PUT/PATCH/DELETE
 * that does not echo the cookie's value in the `X-XSRF-TOKEN` header. A cross-site attacker can
 * force the cookie to be *sent* but can neither read it nor set the header, so the pair cannot be
 * forged. Stateless on purpose: nothing is held in server sessions, so the mechanism survives the
 * planned move to JWT-based auth unchanged.
 *
 * The cookie exists from the first response of a page load (`GET /auth/me`). The one recoverable
 * failure is a write sent before any response has been received (or after the cookie was cleared):
 * the server answers 403 with the `X-CSRF-Failure: true` marker — distinct from an authorization
 * 403 — and callers then `refreshCsrfToken()` (any GET restocks the cookie) and retry once.
 */

/** The readable cookie the server keeps the token in. */
export const CSRF_COOKIE = 'XSRF-TOKEN';

/** The header a state-changing request must echo the cookie's value in. */
export const CSRF_HEADER = 'X-XSRF-TOKEN';

/** Response header the server sets on a 403 that was a CSRF-token failure, not an authz denial. */
export const CSRF_FAILURE_HEADER = 'X-CSRF-Failure';

/** Request methods that never need the token (Spring's CsrfFilter ignores them). */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

/** The current token, straight from the cookie jar; null when the cookie is absent (or no DOM). */
export function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name === CSRF_COOKIE) {
      const value = decodeURIComponent(rest.join('='));
      return value || null;
    }
  }
  return null;
}

/**
 * Headers to add to a request of the given method: the token header for state-changing methods,
 * nothing for safe ones or while the cookie is absent (the server then answers a marked 403,
 * which triggers a refresh + retry).
 */
export function csrfHeaders(method: string | undefined): Record<string, string> {
  const m = (method ?? 'GET').toUpperCase();
  if (SAFE_METHODS.has(m)) return {};
  const token = getCsrfToken();
  return token ? { [CSRF_HEADER]: token } : {};
}

/**
 * True when a response is the server saying "wrong or missing CSRF token" (vs any other denial).
 * The marker is what identifies it; the status is 403 by contract but has arrived as a 401 when
 * the container's error dispatch re-entered the security chain anonymously (see
 * docs/plans/session-timeout-fixes.md, S1), and a marked 401 read as "token expired" signed the
 * user out. Accept the marker on either.
 */
export function isCsrfFailure(status: number, failureHeader: string | null): boolean {
  return (status === 403 || status === 401) && failureHeader === 'true';
}

/**
 * Restock the cookie after a marked 403: any GET makes the server set it (`CsrfCookieFilter`);
 * `/auth/me` is the one endpoint that answers without a session. Resolves true when the cookie
 * is present afterwards.
 */
export async function refreshCsrfToken(): Promise<boolean> {
  try {
    await fetch(`${env.API_BASE}/auth/me`, { credentials: 'include' });
  } catch {
    return false;
  }
  return getCsrfToken() !== null;
}
